import Skeleton from '@carvana/ds-skeleton';
import cx from 'classnames';
import { useContext, useMemo } from 'react';
import { useConfiguration } from '~/context/common/StaticContext';
import StaticContext from '~/context/srp/StaticContext';
import useShowTradeIncentives from '~/hooks/common/selectors/useShowTradeIncentives';
import { PromoBanners } from './constants';
import GeicoBanner from './GeicoBanner';
import {
  useBannerReady,
  useGetGeicoDiscount,
  useGetRootDiscount,
  useHasNewCarTile,
  useNewCarTileMetaData
} from './hooks';
import NewCarBanner from './NewCarBanner';
import PriceDrop from './PriceDrop';
import RootBanner from './RootBanner';
import TradeIncentiveBanner from './TradeIncentive';

function PromoBanner() {
  const { enableNccConditionalPromo } = useConfiguration();
  const { dismissedWSYS } = useContext(StaticContext);
  const geicoDiscount = useGetGeicoDiscount();
  const hasNewCarTile = useHasNewCarTile();
  const newCarTileMetaData = useNewCarTileMetaData();
  const rootDiscount = useGetRootDiscount();
  const showWSYS = useShowTradeIncentives();

  const hasGeicoDiscount = Boolean(geicoDiscount);
  const hasRootDiscount = Boolean(rootDiscount);

  const banner = useMemo(() => {
    switch (true) {
      case enableNccConditionalPromo && hasNewCarTile:
        return PromoBanners.NewCar;
      case hasGeicoDiscount:
        return PromoBanners.Geico;
      case hasRootDiscount:
        return PromoBanners.Root;
      case showWSYS && !dismissedWSYS:
        return PromoBanners.WSYS;
      default:
        return PromoBanners.PriceDrop;
    }
  }, [
    dismissedWSYS,
    enableNccConditionalPromo,
    hasGeicoDiscount,
    hasNewCarTile,
    hasRootDiscount,
    showWSYS
  ]);

  switch (banner) {
    case PromoBanners.NewCar:
      return <NewCarBanner tileMetadata={newCarTileMetaData} />;
    case PromoBanners.Geico:
      return <GeicoBanner />;
    case PromoBanners.Root:
      return <RootBanner />;
    case PromoBanners.WSYS:
      return <TradeIncentiveBanner />;
    default:
      return <PriceDrop />;
  }
}

export default function PromoBannerWrapper() {
  const isBannerReady = useBannerReady();

  return (
    <div className="relative rounded-md lg:rounded-xl">
      <PromoBanner />
      <Skeleton
        className={cx(
          'pointer-events-none absolute inset-0 z-10 !h-full !w-full overflow-hidden !rounded-md transition-all duration-300 lg:!rounded-xl',
          isBannerReady ? 'opacity-0' : 'opacity-100'
        )}
      />
    </div>
  );
}
